/**
 * 截图工具类
 * 提供将DOM元素截图并生成图片或PDF文件的功能
 */

import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * 截图并下载图片
 * @param {HTMLElement} element - 需要截图的DOM元素
 * @param {string} filename - 生成的图片文件名
 * @param {Object} options - 配置选项
 * @param {boolean} options.useCORS - 是否允许跨域图片
 * @param {number} options.scale - 缩放比例
 * @param {string} options.imageType - 图片类型，默认为'image/jpeg'
 * @param {number} options.imageQuality - 图片质量，默认为0.95
 * @returns {Promise<string>} 图片的base64数据URL
 */
export async function captureAndDownloadImage(element, filename = 'screenshot', options = {}) {
    // 默认配置
    const defaultOptions = {
        useCORS: true,
        scale: 2,
        imageType: 'image/jpeg',
        imageQuality: 0.95,
    }

    const config = {...defaultOptions, ...options}

    if (!element) {
        throw new Error('未找到要截图的元素')
    }

    try {
        // 配置html2canvas选项
        const canvasOptions = {
            useCORS: config.useCORS,
            scale: config.scale,
            logging: false,
            letterRendering: true,
        }

        // 生成canvas
        const canvas = await html2canvas(element, canvasOptions)
        const imgData = canvas.toDataURL(config.imageType, config.imageQuality)

        // 创建下载链接
        const link = document.createElement('a')
        link.href = imgData
        link.download = `${filename}.jpg`
        link.click()

        // 返回图片的base64数据URL
        return imgData
    } catch (error) {
        console.error('截图失败:', error)
        throw new Error('截图时出错，请重试')
    }
}

/**
 * 截图并生成PDF
 * @param {HTMLElement} element - 需要截图的DOM元素
 * @param {string} filename - 生成的PDF文件名
 * @param {Object} options - 配置选项
 * @param {boolean} options.useCORS - 是否允许跨域图片
 * @param {number} options.scale - 缩放比例
 * @param {string} options.format - PDF格式，默认为'a4'
 * @param {string} options.orientation - PDF方向，默认为'p'(portrait)
 * @param {string} options.unit - 单位，默认为'mm'
 * @returns {Promise<Blob>} 生成的PDF文件Blob对象
 */
export async function captureAndGeneratePDF(element, filename = 'screenshot', options = {}) {
    // 默认配置
    const defaultOptions = {
        useCORS: true,
        scale: 2,
        format: 'a4',
        orientation: 'p',
        unit: 'mm',
        margin: 10,
        imageType: 'image/jpeg',
        imageQuality: 0.95,
    }

    const config = {...defaultOptions, ...options}

    if (!element) {
        throw new Error('未找到要截图的元素')
    }

    try {
        // 配置html2canvas选项
        const canvasOptions = {
            useCORS: config.useCORS,
            scale: config.scale,
            logging: false,
            letterRendering: true,
        }

        // 生成canvas
        const canvas = await html2canvas(element, canvasOptions)
        const imgData = canvas.toDataURL(config.imageType, config.imageQuality)

        // 创建PDF
        const pdf = new jsPDF(config.orientation, config.unit, config.format)

        // 计算图片尺寸以适应PDF页面
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = pdf.internal.pageSize.getHeight()
        const imgWidth = pdfWidth - (config.margin * 2)
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        // 添加图片到PDF
        pdf.addImage(imgData, config.imageType, config.margin, config.margin, imgWidth, imgHeight)

        // 保存PDF文件
        pdf.save(`${filename}.pdf`)

        // 返回PDF的Blob对象
        return pdf.output('blob')
    } catch (error) {
        console.error('生成PDF失败:', error)
        throw new Error('生成PDF时出错，请重试')
    }
}

/**
 * 滚动截图并生成多页PDF
 * @param {HTMLElement} element - 需要截图的可滚动DOM元素
 * @param {string} filename - 生成的PDF文件名
 * @param {Object} options - 配置选项
 * @param {boolean} options.useCORS - 是否允许跨域图片
 * @param {number} options.scale - 缩放比例
 * @param {string} options.format - PDF格式，默认为'a4'
 * @param {string} options.orientation - PDF方向，默认为'p'(portrait)
 * @param {string} options.unit - 单位，默认为'mm'
 * @param {number} options.margin - 页边距，默认为10
 * @returns {Promise<Blob>} 生成的PDF文件Blob对象
 */
export async function captureScrollableElementAsPDF(element, filename = 'scrollable-screenshot', options = {}) {
    const defaultOptions = {
        useCORS: true,
        scale: 2,
        format: 'a4',
        orientation: 'p',
        unit: 'mm',
        margin: 10,
        imageType: 'image/jpeg',
        imageQuality: 0.95,
        scrollDelay: 100
    }

    const config = {...defaultOptions, ...options}

    if (!element) {
        throw new Error('未找到要截图的元素')
    }

    try {
        // 保存原始滚动位置
        const originalScrollTop = element.scrollTop

        // 获取元素的完整高度和可视区域高度
        const scrollHeight = element.scrollHeight
        const clientHeight = element.clientHeight
        const scrollStep = clientHeight // 每次滚动一个视口高度

        // 存储所有截图的canvas
        const canvases = []

        // 分段截图
        for (let top = 0; top < scrollHeight; top += scrollStep) {
            // 滚动到指定位置
            element.scrollTop = top

            // 等待渲染
            await new Promise(resolve => setTimeout(resolve, config.scrollDelay))

            // 配置html2canvas选项
            const canvasOptions = {
                useCORS: config.useCORS,
                scale: config.scale,
                logging: false,
                letterRendering: true
            }

            // 生成canvas并保存
            const canvas = await html2canvas(element, canvasOptions)
            canvases.push(canvas)
        }

        // 恢复原始滚动位置
        element.scrollTop = originalScrollTop

        // 创建PDF
        const pdf = new jsPDF(config.orientation, config.unit, config.format)
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = pdf.internal.pageSize.getHeight()
        const imgWidth = pdfWidth - (config.margin * 2)

        // 处理每个canvas并添加到PDF
        for (let i = 0; i < canvases.length; i++) {
            const canvas = canvases[i]
            const imgData = canvas.toDataURL(config.imageType, config.imageQuality)

            // 计算图片在PDF中的高度
            const imgHeight = (canvas.height * imgWidth) / canvas.width

            // 如果不是第一页，添加新页面
            if (i > 0) {
                pdf.addPage()
            }

            // 添加图片到PDF
            pdf.addImage(imgData, config.imageType, config.margin, config.margin, imgWidth, imgHeight)
        }

        // 保存PDF文件
        pdf.save(`${filename}.pdf`)

        // 返回PDF的Blob对象
        return pdf.output('blob')
    } catch (error) {
        console.error('滚动截图生成PDF失败:', error)
        throw new Error('滚动截图生成PDF时出错，请重试')
    }
}

export default {
    captureAndDownloadImage,
    captureAndGeneratePDF,
    captureScrollableElementAsPDF
}
